import { UserRole, useUsers } from "@/lib/api/user.ts";
import { getMenteeDisplayName } from "@/lib/redacted-display";
import { cn } from "@/lib/utils.ts";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover.tsx";
import {
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  Command,
  CommandList,
} from "@/components/ui/command.tsx";
import { useState } from "react";
import { Button } from "@/components/ui/button.tsx";
import { CommandLoading } from "cmdk";

interface MenteesSelectProps {
  menteeId: string | null;
  handleFilterChange: (name: string, value: string, label?: string) => void;
}

const encodeValue = (value: string, keywords: string[] = []) =>
  `${keywords.join("|")}|${value}`;
const decodeValue = (value: string) => value.split("|").at(-1) as string;

export function MenteesSelect({
  menteeId,
  handleFilterChange,
}: MenteesSelectProps) {
  const [open, setOpen] = useState(false);
  const { data: mentees, isPending } = useUsers(
    1,
    1000,
    undefined,
    UserRole.MENTEE,
    undefined,
    undefined,
    undefined,
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="justify-between"
        >
          {menteeId
            ? (() => {
                const selected = mentees?.users?.find(
                  (mentee) => mentee.id === menteeId,
                );
                return selected ? getMenteeDisplayName(selected) : "";
              })()
            : "Select teen..."}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start">
        <Command>
          <CommandInput placeholder="Search teens..." />

          <CommandList>
            <CommandEmpty>No teens found.</CommandEmpty>
            {isPending && <CommandLoading>Hang on…</CommandLoading>}

            <CommandGroup>
              <CommandItem
                value="all"
                keywords={["all"]}
                onSelect={() => {
                  handleFilterChange("menteeId", "", "All");
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    menteeId === "" ? "opacity-100" : "opacity-0",
                  )}
                />
                All
              </CommandItem>
              {mentees?.users?.map((mentee) => {
                const displayName = getMenteeDisplayName(mentee);
                return (
                  <CommandItem
                    key={mentee.id}
                    value={encodeValue(mentee.id, [displayName])}
                    keywords={[displayName]}
                    onSelect={(currentValue) => {
                      const decodedValue = decodeValue(currentValue);
                      if (currentValue === menteeId) {
                        handleFilterChange("menteeId", "all", "All");
                      } else {
                        handleFilterChange("menteeId", decodedValue, displayName);
                      }
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        menteeId === mentee.id ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {displayName}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
